import {
  FileType,
  FileNameParserInterface,
  NameGroups,
} from "../const/file-types";

import { EnterpriseFileNameParser } from "./EnterpriseFileNameParser";
import { languageNames } from "./languageFullNames";

const NO_LANGUAGE_DEFINED = "Not Defined";

/**
 * FileNameParser with support for Wonderland asset name parsing.
 *
 * Precedence rules for metadata extraction:
 * Each field (language, type, version, etc.) attempts to extract from fileName first,
 * then falls back to assetName if the fileName parser returns undefined or invalid data.
 *
 * This allows subtitle Language/Type inference from Wonderland ASSET NAME
 * even when filenames don't follow Media Spec naming conventions or lack required fields.
 */
export class FileNameParser implements FileNameParserInterface {
  private parsedFileName: EnterpriseFileNameParser;
  private parsedAssetName: EnterpriseFileNameParser | null;

  public constructor(
    public readonly fileName: string = "",
    public readonly assetName?: string
  ) {
    // Always parse fileName
    this.parsedFileName = new EnterpriseFileNameParser(fileName);

    // Parse assetName if provided
    this.parsedAssetName = assetName
      ? new EnterpriseFileNameParser(assetName)
      : null;
  }

  public isValidLanguageCode() {
    return this.mapLanguageCodeToLanguageName() !== NO_LANGUAGE_DEFINED;
  }

  public mapLanguageCodeToLanguageName(): string {
    const languageCode = this.getLanguageCode();
    return languageCode
      ? languageNames[languageCode] || NO_LANGUAGE_DEFINED
      : NO_LANGUAGE_DEFINED;
  }

  public getTypeAndLanguage(): string {
    const type = this.getFileType();
    const language = this.getLanguageCode() as string;
    return `${type}_${language}`;
  }

  public getVersion(): number | undefined {
    const version = this.parsedFileName.getVersion();
    if (version !== undefined) {
      return version;
    }
    return this.parsedAssetName?.getVersion();
  }

  public getLanguageCode(): string | undefined {
    const languageCode = this.parsedFileName.getLanguageCode();
    if (languageCode !== undefined && languageCode in languageNames) {
      return languageCode;
    }
    return this.parsedAssetName?.getLanguageCode();
  }

  public validateComponentFileName(): boolean {
    // Validate using fileName parser, but allow fallback for language code
    const fileType = this.getFileType();
    const languageCode = this.getLanguageCode();
    return (
      ![FileType.VIDEO, FileType.UNKNOWN].includes(fileType) && !!languageCode
    );
  }

  public getFileDetails(): NameGroups | undefined {
    const details = this.parsedFileName.getFileDetails();
    if (details !== undefined) {
      return details;
    }
    return this.parsedAssetName?.getFileDetails();
  }

  public getFileType(): FileType {
    const fileType = this.parsedFileName.getFileType();
    if (fileType !== FileType.UNKNOWN) {
      return fileType;
    }
    return this.parsedAssetName?.getFileType() ?? FileType.UNKNOWN;
  }

  public validateVideoFileName(): boolean {
    // Video validation should primarily use fileName parser
    return this.parsedFileName.validateVideoFileName();
  }
}
